/**
 * 账号系统改造 (feat-jri/account-redesign) API
 *
 * 对应后端 WebLoginCtroller：
 *   POST /service/user/register/personal      个人注册
 *   POST /service/user/register/enterprise    企业注册（注册即 Root）
 *   GET  /service/user/register/check-company 验证 Company ID
 *   POST /service/user/register/apply-company 申请 Company ID
 *   POST /service/user/password/forget        忘记密码（发送验证码邮件）
 *   POST /service/user/password/reset         重置密码
 *   POST /service/user/password/force-change  首次登录强制改密
 *
 * Phase 2 - 角色权限改造：
 *   POST   /service/user/scope/create           创建范围
 *   PUT    /service/user/scope/update           更新范围
 *   DELETE /service/user/scope/delete           删除范围
 *   GET    /service/user/scope/list             查询企业范围列表
 *   GET    /service/user/scope/listByUser       查询用户范围列表
 *   POST   /service/user/scope/assign           为用户分配范围
 *   POST   /service/user/sub-account/create     创建子账号
 *   PUT    /service/user/sub-account/update     更新子账号角色/范围
 *   PUT    /service/user/sub-account/update-profile  更新账号基本信息（Personal/Enterprise Root 编辑）
 *   DELETE /service/user/sub-account/delete     删除子账号
 *   POST   /service/user/sub-account/reinvite   重新发送邀请
 *   POST   /service/user/root/transfer          Root转让
 *   POST   /service/user/root/unfreeze          Root解封
 *
 * date: 2026-07-08
 */
import httpRequest from '@/utils/request.js'

/** 个人注册 */
export const registerPersonal = (data) =>
  httpRequest.httpPost('/service/user/register/personal', data)

/** 企业注册（注册即 Root） */
export const registerEnterprise = (data) =>
  httpRequest.httpPost('/service/user/register/enterprise', data)

/** 验证 Company ID 是否存在并返回企业名称 */
export const checkCompany = (companyCode) =>
  httpRequest.httpGet({
    url: '/service/user/register/check-company',
    params: { companyCode }
  })

/** 申请 Company ID */
export const applyCompany = (data) =>
  httpRequest.httpPost('/service/user/register/apply-company', data)

/** 忘记密码：发送验证码邮件到用户邮箱 */
export const forgetPassword = (data) =>
  httpRequest.httpPost('/service/user/password/forget', data)

/** 重置密码：通过 token 重置密码 */
export const resetPassword = (data) =>
  httpRequest.httpPost('/service/user/password/reset', data)

/** 首次登录强制改密 */
export const forceChangePassword = (data) =>
  httpRequest.httpPost('/service/user/password/force-change', data)

// ===== Phase 2: 范围管理 =====

/** 创建范围 */
export const createScope = (data) =>
  httpRequest.httpPost('/service/user/scope/create', data)

/** 更新范围 */
export const updateScope = (data) =>
  httpRequest.httpPut('/service/user/scope/update', data)

/** 删除范围 */
export const deleteScope = (id) =>
  httpRequest.httpdelete('/service/user/scope/delete', { id })

/** 查询企业下的范围列表（不传 start/size 时不分页，返回全部） */
export const listScopes = (params) =>
  httpRequest.httpGet({
    url: '/service/user/scope/list',
    params: {
      customerId: params.customerId,
      name: params.name,
      ...(params.start != null ? { iDisplayStart: params.start } : {}),
      ...(params.size != null ? { iDisplayLength: params.size } : {})
    }
  })

/** 查询用户所属的范围列表 */
export const listScopesByUser = (userId) =>
  httpRequest.httpGet({
    url: '/service/user/scope/listByUser',
    params: { userId }
  })

/** 为用户分配范围（覆盖模式） */
export const assignScopes = (userId, scopeIds) =>
  httpRequest.httpPost(`/service/user/scope/assign?userId=${userId}`, scopeIds)

// ===== Phase 2: 子账号管理 =====

/** 创建子账号（生成初始密码 + 邀请记录） */
export const createSubAccount = (data) =>
  httpRequest.httpPost('/service/user/sub-account/create', data)

/** 更新子账号角色和范围 */
export const updateSubAccount = (data) =>
  httpRequest.httpPut('/service/user/sub-account/update', data)

/** 更新账号基本信息（Personal/Enterprise Root 账号编辑：username/mobile/email/status/customerId） */
export const updateAccountProfile = (data) =>
  httpRequest.httpPut('/service/user/sub-account/update-profile', data)

/** 删除子账号 */
export const deleteSubAccount = (userId) =>
  httpRequest.httpdelete('/service/user/sub-account/delete', { userId })

/** 重新发送邀请（生成新密码 + 刷新邀请记录） */
export const reinviteSubAccount = (userId) =>
  httpRequest.httpPost(`/service/user/sub-account/reinvite?userId=${userId}`, {})

/** 批量设置子账号状态（0=禁用, 1=启用） */
export const batchUpdateStatus = (userIds, status) =>
  httpRequest.httpPost('/service/user/sub-account/batch-status', { userIds, status })

/** 管理员创建个人账号（含邀请记录、初始密码、Scene根节点） */
export const createPersonalAccount = (data) =>
  httpRequest.httpPost('/service/user/sub-account/create-personal', data)

/** 管理员创建企业初始账号（含邀请记录、初始密码、Root角色） */
export const createEnterpriseRootAccount = (data) =>
  httpRequest.httpPost('/service/user/sub-account/create-enterprise-root', data)

/**
 * 子账号列表（复用现有用户列表接口，按 customerId 过滤）
 * 对应后端 AccountController: GET /service/account/list
 */
export const listSubAccounts = (params) =>
  httpRequest.httpGet({
    url: '/service/account/list',
    params: {
      iDisplayStart: params.start || 0,
      iDisplayLength: params.size || 20,
      customerId: params.customerId,
      name: params.name,
      account: params.account,
      status: params.accountStatus,
      userType: params.userType
    }
  })

/**
 * 预置角色列表（复用现有角色下拉接口）
 * 对应后端 AccountController: GET /service/account/role
 */
export const listPresetRoles = (customerId) =>
  httpRequest.httpGet({
    url: '/service/account/role',
    params: { type: 0, customerId }
  })

// ===== Phase 2: Root 转让 / 解封 =====

/** Root 转让（四步流程：权限剥离 → 冻结 → 审计 → 会话清理） */
export const transferRoot = (data) =>
  httpRequest.httpPost('/service/user/root/transfer', data)

/** Root 解封（新 Root 恢复原 Root 为普通企业用户） */
export const unfreezeRoot = (data) =>
  httpRequest.httpPost('/service/user/root/unfreeze', data)

// ===== Phase 4: 安全设置 =====

/** 安全设置 - 用户列表 */
export const listSecurityUsers = (params) =>
  httpRequest.httpGet({
    url: '/service/admin/security/users',
    params: {
      companyId: params.companyId,
      status: params.status,
      roleId: params.roleId,
      keyword: params.keyword,
      page: params.page || 0,
      size: params.size || 20
    }
  })

/** 安全设置 - 批量分配角色和范围 */
export const batchAssignRoleScope = (data) =>
  httpRequest.httpPost('/service/admin/security/batch-assign', data)

// ===== Phase 4: 企业后台管理 =====

/** Company ID 申请列表 */
export const listCompanyApply = (params) =>
  httpRequest.httpGet({
    url: '/service/admin/company/apply/list',
    params: {
      status: params.status,
      keyword: params.keyword,
      page: params.page || 0,
      size: params.size || 20
    }
  })

/** 审核 Company ID 申请 */
export const auditCompanyApply = (data) =>
  httpRequest.httpPost('/service/admin/company/apply/audit', data)

/** 批量审核 Company ID 申请 */
export const batchAuditCompanyApply = (data) =>
  httpRequest.httpPost('/service/admin/company/apply/batch-audit', data)

/** 批量删除 Company ID 申请记录 */
export const batchDeleteCompanyApply = (data) =>
  httpRequest.httpPost('/service/admin/company/apply/batch-delete', data)

/** 新增 Company ID 申请（后台录入） */
export const createCompanyApply = (data) =>
  httpRequest.httpPost('/service/admin/company/apply/create', data)

/** 企业列表 */
export const listCompanies = (params) =>
  httpRequest.httpGet({
    url: '/service/admin/company/list',
    params: {
      keyword: params.keyword,
      status: params.status,
      page: params.page || 0,
      size: params.size || 20
    }
  })

/** 企业详情 */
export const getCompanyDetail = (id) =>
  httpRequest.httpGet({ url: `/service/admin/company/${id}`, params: {} })

/** 变更企业状态（激活/禁用） */
export const updateCompanyStatus = (id, status) =>
  httpRequest.httpPut(`/service/admin/company/${id}/status`, { status })

/** 审核记录列表 */
export const listAuditLog = (params) =>
  httpRequest.httpGet({
    url: '/service/admin/company/audit/log',
    params: {
      result: params.result,
      keyword: params.keyword,
      page: params.page || 0,
      size: params.size || 20
    }
  })

// ===== Phase 4: Security Group =====
export const listSecurityGroups = (params) =>
  httpRequest.httpGet({
    url: '/service/admin/security/group/list',
    params: {
      customerId: params.customerId,
      keyword: params.keyword,
      page: params.page || 0,
      size: params.size || 20
    }
  })

export const getSecurityGroupDetail = (id) =>
  httpRequest.httpGet({ url: `/service/admin/security/group/${id}` })

export const createSecurityGroup = (data) =>
  httpRequest.httpPost('/service/admin/security/group', {
    name: data.name,
    description: data.description,
    status: data.status,
    customerId: data.customerId
  })

export const updateSecurityGroup = (id, data) =>
  httpRequest.httpPut(`/service/admin/security/group/${id}`, {
    name: data.name,
    description: data.description,
    status: data.status
  })

export const deleteSecurityGroup = (id) =>
  httpRequest.httpdelete(`/service/admin/security/group/${id}`)

export const assignGroupScope = (id, scopeId) =>
  httpRequest.httpPut(`/service/admin/security/group/${id}/scope`, { scopeId })

export const assignGroupRole = (id, roleId) =>
  httpRequest.httpPut(`/service/admin/security/group/${id}/role`, { roleId })

export const listSecurityGroupMembers = (id, params) =>
  httpRequest.httpGet({
    url: `/service/admin/security/group/${id}/members`,
    params: {
      page: params.page || 0,
      size: params.size || 20
    }
  })

export const addGroupMembers = (id, userIds) =>
  httpRequest.httpPost(`/service/admin/security/group/${id}/members`, { userIds })

export const removeGroupMember = (id, userId) =>
  httpRequest.httpdelete(`/service/admin/security/group/${id}/members/${userId}`)

export const listGroupUserIds = (id) =>
  httpRequest.httpGet({ url: `/service/admin/security/group/${id}/user-ids` })
